import { useState } from "react";
import { openChatPopup } from "@/lib/chatPopup";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Search,
  Phone,
  Mail,
  Crown,
  Loader2,
  X,
  MessageSquare,
  Clock,
  MapPin,
  CalendarDays,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import api, { getProfileImageUrl } from "@/services/api";

import chatService from "@/services/chatService";
import { useAuthStore } from "@/stores/authStore";
import { useMobileDetect } from "@/hooks/useMobileDetect";
import { useNativeApp } from "@/hooks/useNativeApp";

interface UserBrief {
  id: string;
  name: string;
  english_name?: string;
  email: string;
  phone?: string;
  position?: string;
  position_name?: string;
  job_title?: string;
  profile_image?: string;
  attendance_status?: string;  // 금일 출퇴근 상태
  leave_status?: string;  // 금일 휴가 상태
  leave_days?: number;  // 금일 휴가 사용분 (0.5=반차, 1=종일)
  on_business_trip?: boolean;  // 금일 출장 여부 (출근/퇴근과 별개 배지)
  on_field_work?: boolean;  // 금일 외근 여부 (출근/퇴근과 별개 배지)
}

interface Department {
  id: number;
  name: string;
  code: string;
  parent_id?: number;
  order_num: number;
  manager?: UserBrief;
  members: UserBrief[];
  children: Department[];
}

interface OrganizationTree {
  departments: Department[];
  total_employees: number;
}

interface SearchResult extends UserBrief {
  department?: {
    id: number;
    name: string;
  };
}

interface OrganizationSidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Helper function to get attendance badge
const getAttendanceBadge = (status?: string) => {
  if (!status) return null;

  switch (status) {
    case "checked_in":
      return { text: "출근", className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" };
    case "checked_out":
      return { text: "퇴근", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" };
    case "not_checked_in":
      return { text: "미출근", className: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" };
    case "business_trip":
      return { text: "출장", className: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300" };
    default:
      return null;
  }
};

// 출장 배지 스타일 (출근/퇴근·휴가와 별개로 함께 표시됨)
const TRIP_BADGE_CLASS =
  "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300";

// 외근 배지 스타일 (출장과 구분되는 하늘색)
const FIELD_WORK_BADGE_CLASS =
  "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300";

// Helper function to get leave badge
// leaveDays: 금일 휴가 사용분 (0.5=반차, 1=종일) — 반차 라벨은 자체로 0.5를 의미하므로 숫자 생략
const getLeaveBadge = (leaveType?: string, leaveDays?: number) => {
  if (!leaveType) return null;

  const withDays = (label: string) =>
    leaveDays != null && leaveDays > 0 ? `${label} ${leaveDays}` : label;

  switch (leaveType) {
    case "annual":
      return { text: withDays("휴가"), className: "bg-orange-100 text-orange-700" };
    case "half_am":
      return { text: "오전반차", className: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300" };
    case "half_pm":
      return { text: "오후반차", className: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300" };
    case "sick":
      return { text: withDays("병가"), className: "bg-red-100 text-red-700" };
    case "condolence":
      return { text: withDays("경조"), className: "bg-purple-100 text-purple-700" };
    case "official":
      return { text: withDays("공가"), className: "bg-indigo-100 text-indigo-700" };
    case "substitute":
      return { text: withDays("대체휴가"), className: "bg-teal-100 text-teal-700" };
    case "special":
      return { text: withDays("특별휴가"), className: "bg-amber-100 text-amber-700" };
    default:
      return { text: withDays("휴가"), className: "bg-orange-100 text-orange-700" };
  }
};

// 직원 금일 상세 현황
interface MemberTodayStatus {
  attendance_status?: string;
  leave_status?: string;
  leave_days?: number; // 금일 휴가 사용분 (0.5=반차, 1=종일)
  on_business_trip?: boolean; // 금일 출장 여부 (출근/퇴근과 별개 배지)
  on_field_work?: boolean; // 금일 외근 여부 (출근/퇴근과 별개 배지)
  work_status?: string; // normal, late, early_leave
  check_in_time?: string; // "HH:MM"
  check_out_time?: string; // "HH:MM"
  trip_destination?: string;
  trip_purpose?: string;
  trip_start_date?: string; // ISO date
  trip_end_date?: string;
  trip_is_all_day?: boolean;
  trip_start_time?: string;
  trip_end_time?: string;
  leave_reason?: string;
  leave_time_slot?: string;
}

// 근태 상태(지각/조퇴) 라벨
const getWorkStatusLabel = (status?: string) => {
  switch (status) {
    case "late":
      return "지각";
    case "early_leave":
      return "조퇴";
    default:
      return null;
  }
};

// ISO date(YYYY-MM-DD) → "M/D"
const formatMonthDay = (iso?: string) => {
  if (!iso) return "";
  const parts = iso.split("-");
  if (parts.length < 3) return iso;
  return `${parseInt(parts[1], 10)}/${parseInt(parts[2], 10)}`;
};

// 출장 기간 텍스트
const formatTripPeriod = (s: MemberTodayStatus) => {
  if (!s.trip_start_date) return "";
  const start = formatMonthDay(s.trip_start_date);
  const end = formatMonthDay(s.trip_end_date);
  let period = !end || start === end ? start : `${start} ~ ${end}`;
  if (s.trip_is_all_day === false && s.trip_start_time) {
    period += ` ${s.trip_start_time}${s.trip_end_time ? `~${s.trip_end_time}` : ""}`;
  }
  return period;
};

// Tree Node Component
function TreeNode({
  department,
  level = 0,
  onMemberClick,
}: {
  department: Department;
  level?: number;
  onMemberClick: (member: UserBrief, deptName: string) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = department.children.length > 0;
  const memberCount = department.members.length + (department.manager ? 1 : 0);

  return (
    <div className={cn("relative", level > 0 && "ml-4")}>
      {level > 0 && (
        <div className="absolute left-[-12px] top-2.5 w-3 h-px bg-border" />
      )}

      <div className="flex items-center gap-1.5 py-1">
        {hasChildren ? (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-0.5 hover:bg-muted rounded transition-colors"
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </button>
        ) : (
          <span className="w-4" />
        )}

        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <Building2 className="h-3.5 w-3.5 text-primary shrink-0" />
          <span className="font-medium text-sm truncate">{department.name}</span>
          <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4 shrink-0">
            {memberCount}
          </Badge>
        </div>
      </div>

      {isExpanded && (
        <div className={cn("ml-4 border-l border-border", level > 0 && "ml-4")}>
          {department.manager && (
            <button
              onClick={() => onMemberClick(department.manager!, department.name)}
              className="flex items-center gap-1.5 py-1 pl-2 pr-1 hover:bg-muted/50 rounded-r transition-colors w-full text-left"
            >
              <div className="relative">
                <Avatar className="h-5 w-5">
                  {department.manager.profile_image ? (
                    <AvatarImage
                      src={getProfileImageUrl(department.manager.id)}
                      alt={department.manager.name}
                    />
                  ) : null}
                  <AvatarFallback className="text-[8px] bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                    {department.manager.name.slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <Crown className="absolute -top-1 -right-1 h-3 w-3 text-amber-600 dark:text-amber-400 drop-shadow-sm" fill="currentColor" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span className="text-xs truncate">{department.manager.name}</span>
                  {department.manager.english_name && (
                    <span className="text-[10px] text-muted-foreground truncate">
                      ({department.manager.english_name})
                    </span>
                  )}
                  {getAttendanceBadge(department.manager.attendance_status) && (
                    <span className={cn(
                      "px-1 py-0.5 text-[9px] font-medium rounded shrink-0",
                      getAttendanceBadge(department.manager.attendance_status)?.className
                    )}>
                      {getAttendanceBadge(department.manager.attendance_status)?.text}
                    </span>
                  )}
                  {department.manager.on_business_trip && (
                    <span className={cn(
                      "px-1 py-0.5 text-[9px] font-medium rounded shrink-0",
                      TRIP_BADGE_CLASS
                    )}>
                      출장
                    </span>
                  )}
                  {department.manager.on_field_work && (
                    <span className={cn(
                      "px-1 py-0.5 text-[9px] font-medium rounded shrink-0",
                      FIELD_WORK_BADGE_CLASS
                    )}>
                      외근
                    </span>
                  )}
                  {getLeaveBadge(department.manager.leave_status, department.manager.leave_days) && (
                    <span className={cn(
                      "px-1 py-0.5 text-[9px] font-medium rounded shrink-0",
                      getLeaveBadge(department.manager.leave_status, department.manager.leave_days)?.className
                    )}>
                      {getLeaveBadge(department.manager.leave_status, department.manager.leave_days)?.text}
                    </span>
                  )}
                </div>
              </div>
              {(department.manager.position_name || department.manager.position) && (
                <span className="text-[10px] text-muted-foreground shrink-0">
                  {department.manager.position_name || department.manager.position}
                </span>
              )}
            </button>
          )}

          {department.members.map((member) => (
            <button
              key={member.id}
              onClick={() => onMemberClick(member, department.name)}
              className="flex items-center gap-1.5 py-1 pl-2 pr-1 hover:bg-muted/50 rounded-r transition-colors w-full text-left"
            >
              <Avatar className="h-5 w-5">
                {member.profile_image ? (
                  <AvatarImage src={getProfileImageUrl(member.id)} alt={member.name} />
                ) : null}
                <AvatarFallback className="text-[8px] bg-muted">
                  {member.name.slice(0, 2)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span className="text-xs truncate">{member.name}</span>
                  {member.english_name && (
                    <span className="text-[10px] text-muted-foreground truncate">
                      ({member.english_name})
                    </span>
                  )}
                  {getAttendanceBadge(member.attendance_status) && (
                    <span className={cn(
                      "px-1 py-0.5 text-[9px] font-medium rounded shrink-0",
                      getAttendanceBadge(member.attendance_status)?.className
                    )}>
                      {getAttendanceBadge(member.attendance_status)?.text}
                    </span>
                  )}
                  {member.on_business_trip && (
                    <span className={cn(
                      "px-1 py-0.5 text-[9px] font-medium rounded shrink-0",
                      TRIP_BADGE_CLASS
                    )}>
                      출장
                    </span>
                  )}
                  {member.on_field_work && (
                    <span className={cn(
                      "px-1 py-0.5 text-[9px] font-medium rounded shrink-0",
                      FIELD_WORK_BADGE_CLASS
                    )}>
                      외근
                    </span>
                  )}
                  {getLeaveBadge(member.leave_status, member.leave_days) && (
                    <span className={cn(
                      "px-1 py-0.5 text-[9px] font-medium rounded shrink-0",
                      getLeaveBadge(member.leave_status, member.leave_days)?.className
                    )}>
                      {getLeaveBadge(member.leave_status, member.leave_days)?.text}
                    </span>
                  )}
                </div>
              </div>
              {(member.position_name || member.position) && (
                <span className="text-[10px] text-muted-foreground truncate shrink-0">
                  {member.position_name || member.position}
                </span>
              )}
            </button>
          ))}

          {department.children.map((child) => (
            <TreeNode
              key={child.id}
              department={child}
              level={level + 1}
              onMemberClick={onMemberClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// 멤버가 없는 빈 부서를 재귀적으로 필터링
function filterEmptyDepartments(departments: Department[]): Department[] {
  return departments
    .map(dept => ({
      ...dept,
      children: filterEmptyDepartments(dept.children),
    }))
    .filter(dept => {
      const memberCount = dept.members.length + (dept.manager ? 1 : 0);
      return memberCount > 0 || dept.children.length > 0;
    });
}

export default function OrganizationSidebar({ open, onOpenChange }: OrganizationSidebarProps) {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useMobileDetect();
  const { isWebView } = useNativeApp();
  const isInlineMode = isMobile || isTablet || isWebView;
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMember, setSelectedMember] = useState<{
    member: UserBrief;
    deptName: string;
  } | null>(null);
  const [isStartingChat, setIsStartingChat] = useState(false);

  const { data: orgTree, isLoading } = useQuery<OrganizationTree>({
    queryKey: ["organization-chart"],
    queryFn: async () => {
      const response = await api.get("/organization/chart");
      return response.data;
    },
    enabled: open,
  });

  const { data: searchResults, isLoading: isSearching } = useQuery<SearchResult[]>({
    queryKey: ["organization-search", searchQuery],
    queryFn: async () => {
      const response = await api.get(`/organization/search?q=${searchQuery}`);
      return response.data;
    },
    enabled: open && searchQuery.length >= 2,
  });

  // 선택한 직원의 금일 상세 현황 (출퇴근 시간, 출장/반차 상세 등)
  const { data: todayStatus } = useQuery<MemberTodayStatus>({
    queryKey: ["member-today-status", selectedMember?.member.id],
    queryFn: async () => {
      const response = await api.get(
        `/organization/members/${selectedMember!.member.id}/today-status`
      );
      return response.data;
    },
    enabled: !!selectedMember,
    staleTime: 60 * 1000,
  });


  const handleMemberClick = (member: UserBrief, deptName: string) => {
    setSelectedMember({ member, deptName });
  };

  const handleStartChat = async () => {
    if (!selectedMember || !user) return;

    // Don't start chat with yourself
    if (selectedMember.member.id === user.id) return;

    setIsStartingChat(true);
    try {
      // Create or get existing direct chat room
      const room = await chatService.createRoom({
        room_type: "direct",
        member_ids: [selectedMember.member.id],
      });

      // Open chat
      if (isInlineMode) {
        navigate(`/chat/popup/${room.id}`);
        onOpenChange(false);
      } else {
        openChatPopup(room.id);
      }
      setSelectedMember(null);
    } catch (error) {
      console.error("Failed to start chat:", error);
    } finally {
      setIsStartingChat(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-[340px] sm:w-[380px] p-0 z-[10001]" overlayClassName="z-[10000]">
          <SheetHeader className="px-4 py-3 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                <SheetTitle className="text-base">조직도</SheetTitle>
                {orgTree && (
                  <Badge variant="secondary" className="text-xs">
                    {orgTree.total_employees}명
                  </Badge>
                )}
              </div>
            </div>
          </SheetHeader>

          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="이름, 담당업무 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setSearchQuery("")}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>

          <ScrollArea className="h-[calc(100vh-140px)]">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : searchQuery.length >= 2 ? (
              // Search Results
              <div className="p-3">
                {isSearching ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  </div>
                ) : searchResults && searchResults.length > 0 ? (
                  <div className="space-y-1">
                    {searchResults.map((result) => (
                      <button
                        key={result.id}
                        onClick={() => handleMemberClick(result, result.department?.name || "")}
                        className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors w-full text-left"
                      >
                        <Avatar className="h-7 w-7">
                          {result.profile_image ? (
                            <AvatarImage src={getProfileImageUrl(result.id)} alt={result.name} />
                          ) : null}
                          <AvatarFallback className="text-xs bg-muted">
                            {result.name.slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{result.name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {result.department?.name}
                            {(result.position_name || result.position) && ` · ${result.position_name || result.position}`}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground text-sm py-4">
                    검색 결과가 없습니다.
                  </p>
                )}
              </div>
            ) : orgTree && orgTree.departments.length > 0 ? (
              // Organization Tree (빈 부서 제외)
              <div className="p-3">
                {filterEmptyDepartments(orgTree.departments).map((dept) => (
                  <TreeNode
                    key={dept.id}
                    department={dept}
                    onMemberClick={handleMemberClick}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Building2 className="h-8 w-8 mb-2" />
                <p className="text-sm">등록된 부서가 없습니다</p>
              </div>
            )}
          </ScrollArea>

          {/* 색상 범례 */}
          <div className="px-4 py-2 border-t bg-muted/30 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">출근</span>
            <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">퇴근</span>
            <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">미출근</span>
            <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">출장</span>
            <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300">외근</span>
            <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-orange-100 text-orange-700">휴가</span>
            <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300">반차</span>
            <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-red-100 text-red-700">병가</span>
            <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-purple-100 text-purple-700">경조</span>
          </div>
        </SheetContent>
      </Sheet>

      {/* Member Detail Dialog */}
      <Dialog open={!!selectedMember} onOpenChange={() => setSelectedMember(null)}>
        <DialogContent className="sm:w-[calc(100%-2rem)] max-w-sm mx-auto z-[10003]" showOverlay={false}>
          <DialogHeader>
            <DialogTitle>직원 정보</DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Avatar className="h-14 w-14">
                  {selectedMember.member.profile_image ? (
                    <AvatarImage
                      src={getProfileImageUrl(selectedMember.member.id)}
                      alt={selectedMember.member.name}
                    />
                  ) : null}
                  <AvatarFallback className="text-lg bg-primary/10 text-primary">
                    {selectedMember.member.name.slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">
                    {selectedMember.member.name}
                  </h3>
                  {selectedMember.member.english_name && (
                    <p className="text-sm text-muted-foreground">
                      {selectedMember.member.english_name}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    <Badge variant="secondary" className="text-xs">
                      {selectedMember.deptName}
                    </Badge>
                    {(selectedMember.member.position_name || selectedMember.member.position) && (
                      <Badge variant="outline" className="text-xs">
                        {selectedMember.member.position_name || selectedMember.member.position}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* 오늘 현황 */}
              {todayStatus &&
                (todayStatus.attendance_status ||
                  todayStatus.leave_status ||
                  todayStatus.on_business_trip ||
                  todayStatus.on_field_work) && (
                  <div className="space-y-2 pt-3 border-t">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs font-medium text-muted-foreground mr-0.5">
                        오늘 현황
                      </span>
                      {getAttendanceBadge(todayStatus.attendance_status) && (
                        <span
                          className={cn(
                            "px-1.5 py-0.5 text-[10px] font-medium rounded",
                            getAttendanceBadge(todayStatus.attendance_status)?.className
                          )}
                        >
                          {getAttendanceBadge(todayStatus.attendance_status)?.text}
                        </span>
                      )}
                      {todayStatus.on_business_trip && (
                        <span
                          className={cn(
                            "px-1.5 py-0.5 text-[10px] font-medium rounded",
                            TRIP_BADGE_CLASS
                          )}
                        >
                          출장
                        </span>
                      )}
                      {todayStatus.on_field_work && (
                        <span
                          className={cn(
                            "px-1.5 py-0.5 text-[10px] font-medium rounded",
                            FIELD_WORK_BADGE_CLASS
                          )}
                        >
                          외근
                        </span>
                      )}
                      {getLeaveBadge(todayStatus.leave_status, todayStatus.leave_days) && (
                        <span
                          className={cn(
                            "px-1.5 py-0.5 text-[10px] font-medium rounded",
                            getLeaveBadge(todayStatus.leave_status, todayStatus.leave_days)?.className
                          )}
                        >
                          {getLeaveBadge(todayStatus.leave_status, todayStatus.leave_days)?.text}
                        </span>
                      )}
                      {getWorkStatusLabel(todayStatus.work_status) && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                          {getWorkStatusLabel(todayStatus.work_status)}
                        </span>
                      )}
                    </div>

                    {/* 출퇴근 시간 */}
                    {(todayStatus.check_in_time || todayStatus.check_out_time) && (
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span>
                          {todayStatus.check_in_time && (
                            <>출근 {todayStatus.check_in_time}</>
                          )}
                          {todayStatus.check_in_time &&
                            todayStatus.check_out_time && (
                              <span className="text-muted-foreground"> · </span>
                            )}
                          {todayStatus.check_out_time && (
                            <>퇴근 {todayStatus.check_out_time}</>
                          )}
                        </span>
                      </div>
                    )}

                    {/* 출장/외근 상세 */}
                    {(todayStatus.on_business_trip || todayStatus.on_field_work) && (
                      <div className="space-y-1 text-sm">
                        {todayStatus.trip_destination && (
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                            <span className="truncate">
                              {todayStatus.trip_destination}
                            </span>
                          </div>
                        )}
                        {todayStatus.trip_start_date && (
                          <div className="flex items-center gap-2">
                            <CalendarDays className="h-4 w-4 text-muted-foreground shrink-0" />
                            <span>{formatTripPeriod(todayStatus)}</span>
                          </div>
                        )}
                        {todayStatus.trip_purpose && (
                          <p className="text-xs text-muted-foreground pl-6 line-clamp-2">
                            {todayStatus.trip_purpose}
                          </p>
                        )}
                      </div>
                    )}

                    {/* 반차 / 휴가 사유 */}
                    {todayStatus.leave_status && todayStatus.leave_reason && (
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        사유: {todayStatus.leave_reason}
                      </p>
                    )}
                  </div>
                )}

              <div className="space-y-2 pt-3 border-t text-sm">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                  <a
                    href={`mailto:${selectedMember.member.email}`}
                    className="text-primary hover:underline truncate"
                  >
                    {selectedMember.member.email}
                  </a>
                </div>
                {selectedMember.member.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                    <a
                      href={`tel:${selectedMember.member.phone}`}
                      className="text-primary hover:underline"
                    >
                      {selectedMember.member.phone}
                    </a>
                  </div>
                )}
              </div>

              {/* Chat Button */}
              {selectedMember.member.id !== user?.id && (
                <Button
                  className="w-full"
                  onClick={handleStartChat}
                  disabled={isStartingChat}
                >
                  {isStartingChat ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <MessageSquare className="h-4 w-4 mr-2" />
                  )}
                  1:1 채팅하기
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
